import BigNumber from 'bignumber.js';
import {TransactionHolderSchema} from '~/entities/operations-crypto';
import {TwoFaProvider, VerificationMethodType} from '~/features/two-factor';
import {expectEither, HttpClient, PUT, ServerValidationResult, useHttpClient} from '~/library/network-facade';
import {AccountId, BooleanString} from '~/library/types';
import {BlockchainAccountType} from '../model/Accounts';

export type SellTxData = {
    account: AccountId;
    amount: BigNumber;
    fee: number;
    repeated_event?: BooleanString;
    verificationMethod?: VerificationMethodType;
};

export type SellTxRequestParams = SellTxData & {
    provider: TwoFaProvider;
};

export type SellTxRequestBody = SellTxData & {
    account: AccountId;
    amount: BigNumber;
    fee: number;
    repeated_event?: BooleanString;
    verificationMethod?: VerificationMethodType;
    provider: TwoFaProvider;
};

function prepareSellTxRequest(id: AccountId, accountType: BlockchainAccountType, data: SellTxRequestParams) {
    const [init, url] = (() => {
        switch (accountType) {
            case 'crypto':
                return [PUT, `/v1/wallets/${id}/sell`];
            case 'stablecoin':
                return [PUT, `/v1/tokens/${id}/sell`];
        }
    })();

    const {provider, amount, ...rest} = data;

    const body = {
        ...rest,
        amount: amount.toString(),
        two_fa_provider: provider.kind,
        ...(provider.kind === 'sms'
            ? {
                  verificationMethod: provider.method,
                  device_id: window.appDeviceId,
                  app_version: window.appVersion,
              }
            : null),
    };

    return [url, init(body)] as const;
}

function submitSellTxRequest(
    http: HttpClient,
    id: AccountId,
    accountType: BlockchainAccountType,
    data: SellTxRequestBody
) {
    const [url, init] = prepareSellTxRequest(id, accountType, data);

    return http(url, init).then(
        ...expectEither({
            200: TransactionHolderSchema,
            201: TransactionHolderSchema,
            400: ServerValidationResult.to({
                'amount?': 'string',
                'fee?': 'string',
            }),
        })
    );
}

export function useSubmitSellTxRequest(id: AccountId, accountType: BlockchainAccountType) {
    const http = useHttpClient();

    return async (data: SellTxRequestBody) => {
        const res = await submitSellTxRequest(http, id, accountType, data);
        const {status, json} = res;

        switch (status) {
            case 200:
            case 201:
                return {value: json};
            case 400:
                return {problem: json};
            case 'Abort':
                throw res.reason;
        }
    };
}
